import * as React from 'react'
import ReactDOM from 'react-dom'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BUTTON — Flat, compact, Vercel-style
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline' | 'soft'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', isLoading, leftIcon, rightIcon, className, children, disabled, ...props }, ref) => {

    const base = [
      'inline-flex items-center justify-center gap-1.5',
      'font-medium rounded-lg transition-all duration-150',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--brand-orange)]',
      'disabled:opacity-40 disabled:cursor-not-allowed select-none',
      'font-[Raleway]',
    ].join(' ')

    const variants: Record<string, string> = {
      primary:   'bg-[var(--sb-bg)] text-white hover:bg-[#1e1038] active:scale-[0.98]',
      secondary: 'bg-white text-[var(--tx)] border border-[var(--bd)] hover:border-[var(--bd2)] hover:bg-[var(--surface-2)] active:scale-[0.98]',
      ghost:     'bg-transparent text-[var(--tx2)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)] active:scale-[0.98]',
      danger:    'bg-white text-[var(--red)] border border-[var(--bd)] hover:bg-[var(--red-bg)] hover:border-red-200 active:scale-[0.98]',
      outline:   'bg-transparent text-[var(--blue)] border border-[var(--blue)] hover:bg-[var(--blue-bg)] active:scale-[0.98]',
      soft:      'bg-[var(--blue-bg)] text-[var(--blue-d)] hover:bg-[#d0eef8] active:scale-[0.98]',
    }

    const sizes: Record<string, string> = {
      sm:   'h-7 px-2.5 text-[12px]',
      md:   'h-8 px-3 text-[13px]',
      lg:   'h-9 px-4 text-[13.5px]',
      icon: 'h-8 w-8 p-0',
    }

    // Primary uses brand gradient
    const primaryStyle = variant === 'primary'
      ? { background: 'linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-amber) 100%)', color: '#fff' }
      : {}

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(base, variants[variant], sizes[size], className)}
        style={primaryStyle}
        {...props}
      >
        {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : leftIcon}
        {children}
        {!isLoading && rightIcon}
      </button>
    )
  }
)
Button.displayName = 'Button'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// INPUT — Resend-style: 34px, border 0.5px, fond blanc
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftAddon, rightAddon, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            style={{
              display: 'block',
              fontFamily: '"Raleway", sans-serif',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--tx2)',
              marginBottom: 5,
            }}
          >
            {label}
            {props.required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}
          </label>
        )}
        <div className="relative flex items-center">
          {leftAddon && (
            <div className="absolute left-2.5 pointer-events-none" style={{ color: 'var(--tx4)' }}>
              {leftAddon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full text-[13px] transition-all outline-none font-[Raleway]',
              'bg-white border rounded-lg',
              'placeholder:text-[var(--tx4)]',
              error
                ? 'border-[var(--red)] focus:border-[var(--red)] focus:ring-[3px] focus:ring-red-100'
                : 'border-[var(--bd)] focus:border-[var(--blue)] focus:ring-[3px] focus:ring-[rgba(245,124,0,0.10)]',
              leftAddon ? 'pl-8' : 'pl-2.5',
              rightAddon ? 'pr-8' : 'pr-2.5',
              'py-[7px]',
              className,
            )}
            style={{ color: 'var(--tx)' }}
            {...props}
          />
          {rightAddon && (
            <div className="absolute right-2.5 pointer-events-none" style={{ color: 'var(--tx4)' }}>
              {rightAddon}
            </div>
          )}
        </div>
        {error && (
          <p style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 4 }}>{error}</p>
        )}
        {!error && hint && (
          <p style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 4 }}>{hint}</p>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SELECT — supports both `options` prop and native `<option>` children
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  hint?: string
  options?: { value: string | number; label: string }[]
  placeholder?: string
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, className, id, children, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-[12px] font-medium text-[var(--tx2)] mb-1.5"
          >
            {label}
            {props.required && <span className="text-[var(--red)] ml-1">*</span>}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'w-full py-[7px] pl-2.5 text-[13px] transition-all outline-none appearance-none rounded-lg font-[Raleway]',
            'bg-white border',
            error
              ? 'border-[var(--red)]'
              : 'border-[var(--bd)] focus:border-[var(--blue)] focus:ring-[3px] focus:ring-[rgba(245,124,0,0.10)]',
            className,
          )}
          style={{
            color: 'var(--tx)',
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238B87A8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 10px center',
            paddingRight: 32,
          }}
          {...props}
        >
          {children ?? (
            <>
              {placeholder && <option value="">{placeholder}</option>}
              {(options ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </>
          )}
        </select>
        {error && <p className="text-[11.5px] text-[var(--red)] mt-1">{error}</p>}
        {!error && hint && <p className="text-[11.5px] text-[var(--tx3)] mt-1">{hint}</p>}
      </div>
    )
  }
)
Select.displayName = 'Select'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TEXTAREA
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const taId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={taId}
            style={{
              display: 'block',
              fontFamily: '"Raleway", sans-serif',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--tx2)',
              marginBottom: 5,
            }}
          >
            {label}
            {props.required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={taId}
          className={cn(
            'w-full px-2.5 py-2 text-[13px] transition-all outline-none resize-y min-h-[80px] rounded-lg font-[Raleway]',
            'bg-white border',
            error
              ? 'border-[var(--red)]'
              : 'border-[var(--bd)] focus:border-[var(--blue)] focus:ring-[3px] focus:ring-[rgba(245,124,0,0.10)]',
            className,
          )}
          style={{ color: 'var(--tx)' }}
          {...props}
        />
        {error && <p style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 4 }}>{error}</p>}
        {!error && hint && <p style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 4 }}>{hint}</p>}
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// BADGE — Dot + label, compact, Linear-style
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
type BadgeVariant = 'default' | 'blue' | 'violet' | 'success' | 'warning' | 'danger' | 'neutral' | 'purple' | 'info'

const BADGE_STYLES: Record<BadgeVariant, React.CSSProperties> = {
  default: { background: 'var(--blue-bg)',   color: 'var(--blue-d)',   border: '0.5px solid rgba(245,124,0,0.25)' },
  blue:    { background: 'var(--blue-bg)',   color: 'var(--blue-d)',   border: '0.5px solid rgba(245,124,0,0.25)' },
  violet:  { background: 'var(--mauve-bg)',  color: 'var(--mauve-d)',  border: '0.5px solid rgba(100,69,146,0.2)' },
  success: { background: 'var(--green-bg)',  color: 'var(--green-text)', border: '0.5px solid rgba(16,185,129,0.2)' },
  warning: { background: 'var(--amber-bg)',  color: 'var(--amber-text)', border: '0.5px solid rgba(245,158,11,0.2)' },
  danger:  { background: 'var(--red-bg)',    color: 'var(--red-text)',  border: '0.5px solid rgba(239,68,68,0.2)' },
  neutral: { background: 'var(--surface-2)', color: 'var(--tx2)',       border: '0.5px solid var(--bd)' },
  purple:  { background: 'var(--mauve-bg)',  color: 'var(--mauve-d)',  border: '0.5px solid rgba(100,69,146,0.2)' },
  info:    { background: 'var(--blue-bg)',   color: 'var(--blue-d)',   border: '0.5px solid rgba(245,124,0,0.25)' },
}

export interface BadgeProps {
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  dot?: boolean
  children: React.ReactNode
  className?: string
}

export function Badge({ variant = 'default', size = 'sm', dot = false, children, className }: BadgeProps) {
  const style = BADGE_STYLES[variant] ?? BADGE_STYLES['default']
  const dotColor = (style.color as string) ?? 'currentColor'
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-full font-[Raleway]', className)}
      style={{
        ...style,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        padding: size === 'sm' ? '2px 7px' : '3px 9px',
        letterSpacing: '0.01em',
      }}
    >
      {dot && (
        <span style={{
          width: 5, height: 5,
          borderRadius: '50%',
          background: dotColor,
          flexShrink: 0,
        }} />
      )}
      {children}
    </span>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CARD — Flat, border 0.5px, no shadow by default
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface CardProps {
  children: React.ReactNode
  className?: string
  padding?: boolean
}

export function Card({ children, className, padding = true }: CardProps) {
  return (
    <div
      className={cn(padding ? 'p-5' : '', className)}
      style={{
        background: '#fff',
        border: '0.5px solid var(--bd)',
        borderRadius: 12,
      }}
    >
      {children}
    </div>
  )
}

export function CardContent({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('p-5', className)}>{children}</div>
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MODAL — Clean, 0.5px border, subtle blur overlay
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  footer?: React.ReactNode
}

const MODAL_SIZES = { sm: 440, md: 560, lg: 720, xl: 900, '2xl': 1080, full: 99999 }

export function Modal({ open, onClose, title, description, children, size = 'md', footer }: ModalProps) {
  // Lock body scroll + Escape key
  React.useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onEsc) }
  }, [open])

  if (!open) return null

  const isWide = size === 'full' || size === '2xl'

  const modal = (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        display: 'flex', alignItems: isWide ? 'stretch' : 'center', justifyContent: 'center',
        padding: isWide ? 0 : 16,
        isolation: 'isolate',
      }}
    >
      {/* Overlay */}
      <div
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(22,12,38,0.50)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        className="nx-scale-in"
        style={{
          position: 'relative', zIndex: 1,
          width: '100%', maxWidth: isWide ? '100%' : MODAL_SIZES[size],
          background: '#fff',
          borderRadius: isWide ? 0 : 16,
          border: isWide ? 'none' : '0.5px solid var(--bd)',
          boxShadow: isWide ? 'none' : '0 24px 64px rgba(22,12,38,0.18), 0 4px 16px rgba(22,12,38,0.06)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          maxHeight: isWide ? '100vh' : 'calc(100vh - 48px)',
        }}
      >
        {(title || description) && (
          <div style={{
            padding: '18px 24px', borderBottom: '0.5px solid var(--bd)', flexShrink: 0,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
          }}>
            <div>
              {title && (
                <h3 style={{
                  fontFamily: '"Raleway", sans-serif',
                  fontWeight: 700, fontSize: 16,
                  color: 'var(--tx)', margin: 0,
                  letterSpacing: '-0.02em',
                }}>
                  {title}
                </h3>
              )}
              {description && (
                <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 4, lineHeight: 1.4 }}>{description}</p>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                width: 28, height: 28, borderRadius: 8,
                border: '1px solid var(--bd)', background: 'var(--surface-2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--tx3)', flexShrink: 0,
                transition: 'all 0.15s', marginTop: 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-3)'; e.currentTarget.style.color = 'var(--tx)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-2)'; e.currentTarget.style.color = 'var(--tx3)' }}
              aria-label="Fermer"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>{children}</div>
        {footer && (
          <div style={{
            padding: '14px 24px',
            borderTop: '0.5px solid var(--bd)',
            display: 'flex', justifyContent: 'flex-end',
            gap: 8, flexShrink: 0,
            background: 'var(--surface-2)',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  return ReactDOM.createPortal(modal, document.body)
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SPINNER
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function Spinner({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const s = size === 'sm' ? 13 : size === 'lg' ? 26 : 18
  return <Loader2 className={cn('animate-spin', className)} style={{ width: s, height: s, color: 'var(--blue)' }} />
}

export function LoadingSpinner({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  return <Spinner size={size} className={className} />
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// EMPTY STATE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function EmptyState({ icon, title, description, action }: {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '48px 24px', textAlign: 'center',
    }}>
      {icon && (
        <div style={{
          width: 44, height: 44, borderRadius: 11, marginBottom: 14,
          background: 'var(--surface-2)',
          border: '0.5px solid var(--bd)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--tx3)',
        }}>
          {icon}
        </div>
      )}
      <p style={{
        fontFamily: '"Raleway", sans-serif',
        fontWeight: 600, fontSize: 14,
        color: 'var(--tx)', marginBottom: 5,
      }}>
        {title}
      </p>
      {description && (
        <p style={{ fontSize: 13, color: 'var(--tx3)', maxWidth: 300, lineHeight: 1.55 }}>{description}</p>
      )}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ALERT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
type AlertVariant = 'info' | 'success' | 'warning' | 'danger'

const ALERT_STYLES: Record<AlertVariant, { bg: string; border: string; text: string }> = {
  info:    { bg: 'var(--blue-bg)',  border: 'rgba(245,124,0,0.25)',  text: 'var(--blue-d)' },
  success: { bg: 'var(--green-bg)', border: 'rgba(16,185,129,0.25)', text: 'var(--green-text)' },
  warning: { bg: 'var(--amber-bg)', border: 'rgba(245,158,11,0.25)', text: 'var(--amber-text)' },
  danger:  { bg: 'var(--red-bg)',   border: 'rgba(239,68,68,0.25)',  text: 'var(--red-text)' },
}

export function Alert({ variant = 'info', title, children }: { variant?: AlertVariant; title?: string; children: React.ReactNode }) {
  const s = ALERT_STYLES[variant]
  return (
    <div style={{
      borderRadius: 9, padding: '11px 14px',
      background: s.bg,
      border: `0.5px solid ${s.border}`,
      color: s.text,
    }}>
      {title && (
        <p style={{
          fontFamily: '"Raleway", sans-serif',
          fontWeight: 600, fontSize: 12.5,
          marginBottom: 3,
        }}>
          {title}
        </p>
      )}
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>{children}</div>
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TABS — Linear-style: underline indicator, no pill background
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface TabsProps {
  tabs: { id: string; label: string; count?: number }[]
  active: string
  onChange: (id: string) => void
  className?: string
  /** Nom accessible de la liste d'onglets (lu par les lecteurs d'écran). */
  ariaLabel?: string
  /** Préfixe d'identifiants : relie chaque onglet au panneau `${idPrefix}-panel`. */
  idPrefix?: string
  /** Libellé lu après le compteur (ex. « présents »), masqué visuellement. */
  countLabel?: string
}

/**
 * Onglets WAI-ARIA (tablist / tab) : tabindex itinérant, flèches gauche/droite,
 * Début/Fin. Défilement horizontal quand les onglets dépassent (mobile).
 * Le panneau associé porte id=`${idPrefix}-panel`, role="tabpanel" et
 * aria-labelledby=`${idPrefix}-tab-${active}`.
 */
export function Tabs({ tabs, active, onChange, className, ariaLabel, idPrefix, countLabel }: TabsProps) {
  const listRef = React.useRef<HTMLDivElement>(null)

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || !tabs.length) return
    const current = Math.max(0, tabs.findIndex((t) => t.id === active))
    let next: number | null = null
    if (e.key === 'ArrowRight') next = (current + 1) % tabs.length
    else if (e.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    if (next === null) return
    e.preventDefault()
    onChange(tabs[next].id)
    const el = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]
    el?.focus()
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn('flex overflow-x-auto overflow-y-hidden overscroll-x-contain', className)}
      // Filet en ombre interne (peinte sous les onglets) : le trait actif le recouvre
      // sans marge négative, que l'overflow du défilement horizontal rognerait.
      style={{ boxShadow: 'inset 0 -0.5px 0 var(--bd)', gap: 0, scrollbarWidth: 'thin' }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={idPrefix ? `${idPrefix}-tab-${tab.id}` : undefined}
            aria-selected={isActive}
            aria-controls={idPrefix ? `${idPrefix}-panel` : undefined}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className="shrink-0 whitespace-nowrap rounded-t-md hover:!text-[var(--tx)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--blue)]"
            style={{
              padding: '8px 14px',
              minHeight: 44,
              border: 'none',
              borderBottom: isActive ? '2px solid var(--blue)' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: '"Raleway", sans-serif',
              fontWeight: isActive ? 600 : 400,
              fontSize: 13,
              transition: 'all 0.15s',
              background: 'transparent',
              color: isActive ? 'var(--tx)' : 'var(--tx3)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{
                fontSize: 10, fontFamily: '"Raleway", sans-serif', fontWeight: 600,
                padding: '1px 5px', borderRadius: 99,
                background: isActive ? 'var(--blue-bg)' : 'var(--surface-2)',
                color: isActive ? 'var(--blue-d)' : 'var(--tx3)',
              }}>
                {tab.count}
                {countLabel && <span className="sr-only"> {countLabel}</span>}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STAT CARD — Compact, flat, trend chip top-right
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function StatCard({ label, value, sub, icon, trend, color = 'blue' }: {
  label: string
  value: string | number
  sub?: string
  icon?: React.ReactNode
  trend?: { value: number; label?: string }
  color?: 'blue' | 'violet' | 'success' | 'warning'
}) {
  const colors: Record<string, { bg: string; icon: string }> = {
    blue:    { bg: 'var(--blue-bg)',  icon: 'var(--blue)' },
    violet:  { bg: 'var(--mauve-bg)', icon: 'var(--mauve)' },
    success: { bg: 'var(--green-bg)', icon: 'var(--green)' },
    warning: { bg: 'var(--amber-bg)', icon: 'var(--amber)' },
  }
  const c = colors[color]
  const isUp = trend && trend.value >= 0

  return (
    <div style={{
      background: '#fff',
      border: '0.5px solid var(--bd)',
      borderRadius: 12,
      padding: '14px 16px',
      position: 'relative',
    }}>
      {/* Top row: label + icon + trend */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon && (
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: c.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: c.icon, flexShrink: 0,
            }}>
              {icon}
            </div>
          )}
          <p style={{
            fontFamily: '"Raleway", sans-serif',
            fontSize: 12, fontWeight: 500,
            color: 'var(--tx3)',
          }}>
            {label}
          </p>
        </div>
        {trend !== undefined && (
          <span style={{
            fontSize: 11, fontWeight: 600, fontFamily: '"Raleway", sans-serif',
            padding: '2px 6px', borderRadius: 99,
            background: isUp ? 'var(--green-bg)' : 'var(--red-bg)',
            color: isUp ? 'var(--green-text)' : 'var(--red-text)',
          }}>
            {isUp ? '↑' : '↓'} {Math.abs(trend.value)}%
          </span>
        )}
      </div>
      {/* Value */}
      <p style={{
        fontFamily: '"Raleway", sans-serif',
        fontWeight: 700, fontSize: 24,
        color: 'var(--tx)', lineHeight: 1,
        letterSpacing: '-0.025em',
      }}>
        {value}
      </p>
      {sub && (
        <p style={{ fontSize: 12, color: 'var(--tx3)', marginTop: 4 }}>{sub}</p>
      )}
      {trend?.label && (
        <p style={{ fontSize: 11, color: 'var(--tx4)', marginTop: 3 }}>{trend.label}</p>
      )}
    </div>
  )
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AVATAR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export interface AvatarProps {
  firstName?: string
  lastName?: string
  src?: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
}

export function Avatar({ firstName, lastName, src, size = 'md', className }: AvatarProps) {
  const sizes = { xs: 22, sm: 26, md: 32, lg: 40 }
  const font  = { xs: 8,  sm: 9,  md: 11, lg: 14 }
  const px = sizes[size]
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()

  return (
    <div
      className={cn('rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden', className)}
      style={{
        width: px, height: px,
        background: src ? 'transparent' : 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))',
        fontSize: font[size], fontWeight: 600, color: '#fff',
        fontFamily: '"Raleway", sans-serif',
      }}
    >
      {src
        ? <img src={src} alt={initials} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : initials
      }
    </div>
  )
}

export { KpiCard } from './KpiCard'
export type { KpiCardProps } from './KpiCard'
export { AsyncCombobox, type AsyncComboboxProps } from './AsyncCombobox'
export { FilePreviewModal, downloadFromUrl, errMessage, type FilePreviewModalProps, type InlineUrlResult } from './FilePreviewModal'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// MODAL OVERLAY — Portalled backdrop for inline modals
// Wraps children in a fixed overlay that is always above sidebar
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function ModalOverlay({
  children,
  onClose,
  className = '',
}: {
  children: React.ReactNode
  onClose?: () => void
  className?: string
}) {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  return ReactDOM.createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[99999] flex items-center justify-center p-4',
        className,
      )}
      style={{ isolation: 'isolate' }}
    >
      <div
        className="absolute inset-0 bg-[rgba(22,12,38,0.50)] backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-[1]" style={{ display: 'contents' }}>
        {children}
      </div>
    </div>,
    document.body,
  )
}
